import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './theme.css';

// 1. Context Provider
import { AuthProvider, useAuth } from './context/AuthContext';

// 2. Public Pages (Marketing / Legal)
import Home from './Home';
import LegalPage from './LegalPage';

// 3. Auth Pages
import SignIn from './pages/SignIn';
import Registration from './pages/Register';
import GuestDashboard from './pages/GuestDashboard';

// 4. Protected App Pages
import StudentDashboard from './pages/StudentDashboard';
import Settings from './pages/Settings';
import Support from './pages/Support';
import Notices from './pages/Notices';
import Feedback from './pages/Feedback';
import About from './pages/About';
import Rules from './pages/Rules';
import InstallAppPrompt from './components/InstallAppPrompt';

// --- PROTECTED ROUTE GUARD ---
// This acts like your Android intent checks. If Session.studentId is missing,
// it bounces the user back to the login screen automatically.
const ProtectedRoute = ({ children }) => {
  const { studentId, isInitializing } = useAuth();

  if (isInitializing) {
    return <div role="status" aria-label="Loading session" />;
  }
  
  if (!studentId) {
    return <Navigate to="/app/signin" replace />;
  }
  
  return children;
};

// The installed PWA can be launched at a public URL. Wait for IndexedDB
// hydration, then keep an existing student session out of the home/login UI.
const PublicOnlyRoute = ({ children }) => {
  const { studentId, studentToken, isInitializing } = useAuth();

  if (isInitializing) {
    return <div role="status" aria-label="Loading session" />;
  }

  if (studentId || studentToken) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    // Wrapping the Router in AuthProvider makes global state available everywhere
    <AuthProvider>
      <Router>
        <InstallAppPrompt />
        <Routes>
          
          {/* --- PUBLIC MARKETING ROUTES --- */}
          <Route path="/" element={<PublicOnlyRoute><Home /></PublicOnlyRoute>} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/app/home" element={<GuestDashboard />} />
          <Route path="/app/guest" element={<GuestDashboard />} />
          
          {/* --- AUTH ROUTES --- */}
          <Route path="/app/signin" element={<PublicOnlyRoute><SignIn /></PublicOnlyRoute>} />
          <Route path="/login" element={<PublicOnlyRoute><SignIn /></PublicOnlyRoute>} />
          <Route path="/app/register" element={<Registration />} />

          {/* --- PROTECTED APP ROUTES --- */}
          <Route 
            path="/app/dashboard" 
            element={<ProtectedRoute><StudentDashboard /></ProtectedRoute>} 
          />
          <Route 
            path="/app/settings" 
            element={<ProtectedRoute><Settings /></ProtectedRoute>} 
          />
          <Route 
            path="/app/support" 
            element={<ProtectedRoute><Support /></ProtectedRoute>} 
          />
          <Route 
            path="/app/notices" 
            element={<ProtectedRoute><Notices /></ProtectedRoute>} 
          />
          <Route 
            path="/app/feedback" 
            element={<ProtectedRoute><Feedback /></ProtectedRoute>} 
          />
          <Route 
            path="/app/about" 
            element={<ProtectedRoute><About /></ProtectedRoute>} 
          />
          <Route 
            path="/app/rules" 
            element={<ProtectedRoute><Rules /></ProtectedRoute>} 
          />
          
          {/* --- CATCH-ALL ROUTE --- */}
          {/* If a user types a URL that doesn't exist, send them home */}
          <Route path="*" element={<Navigate to="/" replace />} />
          
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
